<template>
  <div class="container-fluid py-4">
    <!-- Header with Tabs and Create Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'default' }"
            @click="setActiveTab('default')"
            type="button"
          >
            Default
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'byusers' }"
            @click="setActiveTab('byusers')"
            type="button"
          >
            By Users
          </button>
        </li>
      </ul>
      
      <!-- Create Button - Only show for Default tab -->
      <button
        v-if="activeTab === 'default'"
        class="btn btn-primary"
        @click="openCreateModal"
        :disabled="!canCreate('main')"
      >
        <i class="fa fa-plus me-2"></i>
        Create New Prompt Variable
      </button>
    </div>

    <!-- Tab Content -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">
          {{ currentTabTitle }}
        </h5>
      </div>
      
      <div class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading {{ activeTab === 'default' ? 'Default' : 'By Users' }} prompt variables...</p>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          <div class="d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div class="flex-grow-1">
              <strong>Error:</strong> {{ error }}
            </div>
            <button class="btn btn-sm btn-outline-danger" @click="fetchPromptVariables">
              <i class="fa fa-refresh"></i> Retry
            </button>
          </div>
        </div>
        
        <!-- Prompt Variables Table -->
        <div v-else-if="promptVariables.length > 0">
          <CommonTable
            :tableObj="tableConfig"
            :title="''"
            permissionKey="main"
            @edit="handleEdit"
            @delete="handleDelete"
            @view="handleView"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="mb-3">
            <i class="fa fa-code fa-3x text-muted"></i>
          </div>
          <h5 class="text-muted">No {{ activeTab === 'default' ? 'Default' : 'By Users' }} Prompt Variables Found</h5>
          <p class="text-muted">
            {{ activeTab === 'default' 
              ? 'No default prompt variables are available.' 
              : 'No By Users prompt variables are available.' 
            }}
          </p>
          <div class="mt-3">
            <button 
              v-if="activeTab === 'default'"
              class="btn btn-primary me-2" 
              @click="openCreateModal"
              :disabled="!canCreate('main')"
            >
              <i class="fa fa-plus me-1"></i>
              Create Prompt Variable
            </button>
            <button class="btn btn-outline-secondary" @click="fetchPromptVariables">
              <i class="fa fa-refresh me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Footer -->
      <div v-if="promptVariables.length > 0" class="card-footer bg-light">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Showing {{ promptVariables.length }} {{ activeTab === 'default' ? 'default' : 'By Users' }} prompt variable{{ promptVariables.length !== 1 ? 's' : '' }}
          </small>
          <button class="btn btn-sm btn-outline-secondary" @click="fetchPromptVariables">
            <i class="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>
  </div>

  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    @shown="onCreateModalShown"
    :save-button-disabled="isCreateFormValid"
    dialog-class="modal-xl"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
      
      <!-- Variable Picker for Create -->
      <div class="mt-4 variable-picker-wrapper">
        <h6>Add Existing Variables</h6>
        <VariablePicker 
          v-model="newItemData.selectedVariables"
          :current-category-id="newItemData.prompt_category_id" 
          :variables="allVariables"
          @update:modelValue="(value) => { console.log('🎯 VariablePicker emitted update:', value); newItemData.selectedVariables = value; }"
        />
      </div>
    </template>
  </BaseModal>

  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    @shown="onEditModalShown"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
    dialog-class="modal-xl"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
      
      <!-- Variable Picker for Edit -->
      <div class="mt-4 variable-picker-wrapper" v-if="!isViewMode">
        <h6>Add Existing Variables</h6>
        <VariablePicker 
          v-model="editingItemData.selectedVariables"
          :current-category-id="editingItemData.prompt_category_id"
          :variables="allVariables"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, defineAsyncComponent, nextTick } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue"; 
import apiService from "@/services/apiService";
import CommonTable from "@/components/commen/table/CommonTable.vue";
import VariablePicker from "@/components/commen/modal/VariablePicker.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canCreate } = usePermissions();

const API_RESOURCE = "/common/prompt-variables";
const pageTitle = "Prompt Variables";
const title = "Prompt Variables List";

const moduleOptions = ref<Array<{ _id: string; title: string }>>([]);
const typeOptions = ref<Array<{ _id: string; title: string }>>([]);
const allVariables = ref<any[]>([]);

const crud = useCrud(API_RESOURCE);

// Tab state
const activeTab = ref<'default' | 'byusers'>('default');
const isLoading = ref(false);
const error = ref<string | null>(null);
const promptVariables = ref<any[]>([]);
const toSlug = (s: string) =>
  (s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

// Variable management functions
// Function to automatically update raw description when variables are selected/deselected
const updateRawDescriptionWithSelectedVariables = (formData: any, newVars: any[] | undefined, oldVars: any[] | undefined) => {
  console.log('updateRawDescriptionWithSelectedVariables called:', { newVars, oldVars, rawDesc: formData.raw_description });
  
  // Handle undefined arrays
  const safeNewVars = newVars || [];
  const safeOldVars = oldVars || [];
  
  let rawDesc = formData.raw_description || '';
  
  // Find newly selected variables (in newVars but not in oldVars)
  const addedVars = safeNewVars.filter(newVar => 
    !safeOldVars.some(oldVar => oldVar._id === newVar._id)
  );
  
  // Find deselected variables (in oldVars but not in newVars)
  const removedVars = safeOldVars.filter(oldVar => 
    !safeNewVars.some(newVar => newVar._id === oldVar._id)
  );
  
  console.log('Variables change:', { addedVars, removedVars });
  
  // Add slugs for newly selected variables
  addedVars.forEach(variable => {
    if (variable && variable.slug && variable.slug.trim()) {
      const slug = `{${variable.slug}}`;
      console.log('Adding slug:', slug, 'to description:', rawDesc);
      if (!rawDesc.includes(slug)) {
        rawDesc = rawDesc + (rawDesc.trim() ? ' ' : '') + slug;
        console.log('Updated description after adding:', rawDesc);
      }
    }
  });
  
  // Remove slugs for deselected variables
  removedVars.forEach(variable => {
    if (variable && variable.slug && variable.slug.trim()) {
      const slug = `{${variable.slug}}`;
      console.log('Removing slug:', slug, 'from description:', rawDesc);
      // Use a more precise regex to remove the slug and surrounding spaces
      const escapedSlug = slug.replace(/[{}]/g, '\\$&');
      rawDesc = rawDesc.replace(new RegExp(`\\s*${escapedSlug}\\s*`, 'g'), ' ').replace(/\s+/g, ' ').trim();
      console.log('Updated description after removing:', rawDesc);
    }
  });
  
  console.log('Final raw description update:', rawDesc);
  formData.raw_description = rawDesc;
};

const updateDescriptionWithVariables = (formData: any) => {
  console.log('📝 updateDescriptionWithVariables called with:', {
    raw_description: formData.raw_description,
    selectedVariables: formData.selectedVariables?.map((v: any) => ({ title: v.title, slug: v.slug })) || []
  });
  
  const baseDescription = (formData.raw_description || '').trim();
  const selectedVars = formData.selectedVariables || [];
  
  console.log('📝 Base description:', baseDescription);
  console.log('📝 Selected vars count:', selectedVars.length);
  
  // Replace variable slugs with their actual descriptions
  let finalDescription = baseDescription;
  
  if (selectedVars && selectedVars.length > 0) {
    selectedVars.forEach((variable: any) => {
      if (variable && variable.slug && variable.description) {
        const slug = `{${variable.slug}}`;
        console.log('📝 Replacing slug:', slug, 'with description:', variable.description);
        
        // Replace all occurrences of the slug with the variable's description
        const escapedSlug = slug.replace(/[{}]/g, '\\$&');
        finalDescription = finalDescription.replace(new RegExp(escapedSlug, 'g'), variable.description);
      }
    });
    
    console.log('📝 After slug replacement:', finalDescription);
  } else {
    console.log('📝 No variables selected, using base description as-is');
  }
  
  // Update description
  formData.description = finalDescription;
  console.log('📝 FINAL RESULT - Updated description to:', finalDescription);
  console.log('📝 Form data description field is now:', formData.description);
};

// Computed properties
const currentTabTitle = computed(() => 
  activeTab.value === 'default' ? 'Default Prompt Variables' : 'By Users Prompt Variables'
);

// Process prompt variables data for table display
const processedPromptVariables = computed(() => 
  promptVariables.value.map(variable => ({
    ...variable,
    module_title: variable.prompt_category_id?.title || 
                  (typeof variable.prompt_category_id === 'string' 
                    ? moduleOptions.value.find(m => m._id === variable.prompt_category_id)?.title 
                    : 'Unknown Category'),
    type_title: variable.type_id?.title || 
                (typeof variable.type_id === 'string' 
                  ? typeOptions.value.find(t => t._id === variable.type_id)?.title 
                  : 'No Type')
  }))
);

// Updated table configuration to use processed data and conditional actions
const tableConfig = computed(() => ({
  columns: [
    { name: "Title", field: "title", sortable: true, width:"160px" },
    { name: "Category", field: "module_title", sortable: true, width:"160px"  },
    { name: "Type", field: "type_title", sortable: true, width:"130px"  },
    { name: "Prompt", field: "description", sortable: true },
  ],
  data: processedPromptVariables.value,
  actions: activeTab.value === 'default' ? [true] : [false], // Only show actions for default tab
  loading: isLoading.value
}));

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  prompt_category_id: "",
  type_id: null as string | null,
  title: "",
  description: "",
  raw_description: "",
  slug: "",
  selectedVariables: [] as Array<{_id: string; title: string; description: string; slug: string}>,
  // NEW: checkboxes (default false)
  filterable: false,
  visible_on_card: false,
});

const editingItemData = ref({
  _id: "",
  prompt_category_id: "",
  type_id: null as string | null,
  title: "",
  description: "",
  raw_description: "",
  slug: "",
  selectedVariables: [] as Array<{_id: string; title: string; description: string; slug: string}>,
  // NEW: checkboxes
  filterable: false,
  visible_on_card: false,
});

// Function to parse existing description and extract raw description with variables
const parseExistingDescription = (finalDescription: string, allVariables: any[]) => {
  console.log('🔍 Parsing existing description:', finalDescription);
  console.log('🔍 Available variables for parsing:', allVariables?.length || 0);
  
  let rawDescription = finalDescription;
  const selectedVars: any[] = [];
  
  // Try to reverse-engineer the original raw description by finding variable descriptions
  // and replacing them back with slugs
  if (allVariables && allVariables.length > 0) {
    allVariables.forEach((variable: any) => {
      if (variable.description && variable.slug) {
        // Check if this variable's description appears in the final description
        if (finalDescription.includes(variable.description)) {
          console.log(`🔍 Found variable "${variable.title}" description in final prompt`);
          
          // Replace the description with the slug
          const slug = `{${variable.slug}}`;
          rawDescription = rawDescription.replace(variable.description, slug);
          
          // Add to selected variables
          selectedVars.push({
            _id: variable._id,
            title: variable.title,
            description: variable.description,
            slug: variable.slug
          });
        }
      }
    });
  }
  
  console.log('🔍 Parsed result:', { rawDescription, selectedVars: selectedVars.map(v => v.title) });
  
  return {
    rawDescription: rawDescription,
    selectedVars: selectedVars
  };
};

const isCardCategoryCreate = computed(() => {
  const sel = moduleOptions.value.find(m => m._id === newItemData.value.prompt_category_id);
  return (sel?.title || "").trim().toLowerCase() === "card";
});
const isCardCategoryEdit = computed(() => {
  const sel = moduleOptions.value.find(m => m._id === editingItemData.value.prompt_category_id);
  return (sel?.title || "").trim().toLowerCase() === "card";
});

// Watch for raw description changes to auto-deselect variables when slugs are removed
watch(() => newItemData.value.raw_description, (newDesc) => {
  console.log('🔍 Create form - raw_description changed:', newDesc);
  console.log('🔍 Current selected variables:', newItemData.value.selectedVariables?.map(v => ({ title: v.title, slug: v.slug })));
  
  // Auto-deselect variables whose slugs were manually removed
  if (newItemData.value.selectedVariables && newItemData.value.selectedVariables.length > 0) {
    console.log('🔍 Checking which variables should remain selected...');
    
    const remainingVars = newItemData.value.selectedVariables.filter(variable => {
      const slug = `{${variable.slug}}`;
      const isPresent = newDesc && newDesc.includes(slug);
      console.log(`🔍 Variable "${variable.title}" with slug "${slug}" is ${isPresent ? 'PRESENT' : 'MISSING'} in: "${newDesc}"`);
      return isPresent;
    });
    
    console.log('🔍 Variables that should remain:', remainingVars.map(v => v.title));
    
    if (remainingVars.length !== newItemData.value.selectedVariables.length) {
      console.log('🔄 Auto-deselecting variables with removed slugs');
      console.log('Before:', newItemData.value.selectedVariables.map(v => v.title));
      console.log('After:', remainingVars.map(v => v.title));
      
      // Update selected variables
      newItemData.value.selectedVariables = remainingVars;
      
      // Force update the final prompt after deselection
      nextTick(() => {
        console.log('🔄 Force updating final prompt after auto-deselection');
        console.log('🔄 Selected variables after nextTick:', newItemData.value.selectedVariables?.map(v => v.title));
        updateDescriptionWithVariables(newItemData.value);
      });
      return; // Don't update twice
    }
  }
  
  updateDescriptionWithVariables(newItemData.value);
}, { immediate: true });

// Reactive description updates handled by watchers

watch(() => newItemData.value.title, (val) => {
  newItemData.value.slug = toSlug(val);
});
watch(() => editingItemData.value.title, (val) => {
  editingItemData.value.slug = toSlug(val);
});

// Watch for variable selection changes
watch(() => newItemData.value.selectedVariables, (newVars, oldVars) => {
  console.log('🔥 CREATE WATCHER FIRED 🔥', { newVars, oldVars, currentRawDesc: newItemData.value.raw_description });
  
  // Simple direct approach - just add all selected variable slugs
  if (newVars && newVars.length > 0) {
    let baseText = newItemData.value.raw_description || '';
    // Remove any existing slugs first
    baseText = baseText.replace(/\{[^}]+\}/g, '').trim();
    
    // Add all selected variable slugs
    console.log('Processing variables for slugs:', newVars.map(v => ({ title: v.title, slug: v.slug })));
    const slugs = newVars.filter(v => v.slug).map(v => `{${v.slug}}`);
    console.log('Generated slugs:', slugs);
    if (slugs.length > 0) {
      baseText = baseText + (baseText ? ' ' : '') + slugs.join(' ');
      console.log('Combined baseText with slugs:', baseText);
    }
    
    console.log('Directly updating raw_description to:', baseText);
    newItemData.value.raw_description = baseText;
    console.log('After update - raw_description is now:', newItemData.value.raw_description);
  } else {
    // No variables selected, remove slugs
    let baseText = newItemData.value.raw_description || '';
    baseText = baseText.replace(/\{[^}]+\}/g, '').trim();
    console.log('No variables selected, updating raw_description to:', baseText);
    newItemData.value.raw_description = baseText;
    console.log('After clearing - raw_description is now:', newItemData.value.raw_description);
  }
  
  updateDescriptionWithVariables(newItemData.value);
}, { deep: true, immediate: true });

watch(() => editingItemData.value.selectedVariables, (newVars, oldVars) => {
  console.log('Edit form - selectedVariables changed:', { newVars, oldVars, currentRawDesc: editingItemData.value.raw_description });
  
  // Simple direct approach - just add all selected variable slugs
  if (newVars && newVars.length > 0) {
    let baseText = editingItemData.value.raw_description || '';
    // Remove any existing slugs first
    baseText = baseText.replace(/\{[^}]+\}/g, '').trim();
    
    // Add all selected variable slugs
    console.log('Processing variables for slugs:', newVars.map(v => ({ title: v.title, slug: v.slug })));
    const slugs = newVars.filter(v => v.slug).map(v => `{${v.slug}}`);
    console.log('Generated slugs:', slugs);
    if (slugs.length > 0) {
      baseText = baseText + (baseText ? ' ' : '') + slugs.join(' ');
      console.log('Combined baseText with slugs:', baseText);
    }
    
    console.log('Directly updating raw_description to:', baseText);
    editingItemData.value.raw_description = baseText;
    console.log('After update - raw_description is now:', editingItemData.value.raw_description);
  } else {
    // No variables selected, remove slugs
    let baseText = editingItemData.value.raw_description || '';
    baseText = baseText.replace(/\{[^}]+\}/g, '').trim();
    console.log('No variables selected, updating raw_description to:', baseText);
    editingItemData.value.raw_description = baseText;
    console.log('After clearing - raw_description is now:', editingItemData.value.raw_description);
  }
  
  updateDescriptionWithVariables(editingItemData.value);
}, { deep: true, immediate: true });



watch(() => editingItemData.value.raw_description, (newDesc) => {
  console.log('Edit form - raw_description changed:', newDesc);
  
  // Auto-deselect variables whose slugs were manually removed
  if (editingItemData.value.selectedVariables && editingItemData.value.selectedVariables.length > 0) {
    const remainingVars = editingItemData.value.selectedVariables.filter(variable => {
      const slug = `{${variable.slug}}`;
      return newDesc && newDesc.includes(slug);
    });
    
    if (remainingVars.length !== editingItemData.value.selectedVariables.length) {
      console.log('🔄 Auto-deselecting variables with removed slugs');
      console.log('Before:', editingItemData.value.selectedVariables.map(v => v.title));
      console.log('After:', remainingVars.map(v => v.title));
      editingItemData.value.selectedVariables = remainingVars;
      
      // Force update the final prompt after deselection
      nextTick(() => {
        console.log('🔄 Force updating final prompt after auto-deselection');
        updateDescriptionWithVariables(editingItemData.value);
      });
      return; // Don't update twice
    }
  }
  
  updateDescriptionWithVariables(editingItemData.value);
}, { immediate: true });

// Variable selection handlers are now handled by watchers automatically
// --- Form Fields ---
const createFormFields = computed(() => {
  const fields: any[] = [
    {
      key: "prompt_category_id",
      label: "Prompt Category",
      type: "select",
      required: true,
      placeholder: "Select Category",
      options: moduleOptions.value
    },
    {
      key: "title",
      label: "Title",
      type: "text",
      required: true,
      placeholder: "Enter title"
    },
    {
      key: "type_id",
      label: "Type",
      type: "select",
      required: true,
      placeholder: "Select Type",
      options: typeOptions.value
    },
    {
      key: "raw_description",
      label: "Base Prompt Description",
      type: "textarea",
      required: true,
      placeholder: "Enter base prompt description"
    },
    {
      key: "description",
      label: "Final Prompt (Auto-generated)",
      type: "textarea",
      required: false,
      readonly: true,
      placeholder: "This will be auto-generated from base description + selected variables"
    },
  ];

  // Only show these when Category is "Card"
  if (isCardCategoryCreate.value) {
    fields.push(
      {
        key: "filterable",
        label: "Filterable",
        type: "checkbox",
        required: false,
        readonly: isViewMode.value,
      },
      {
        key: "visible_on_card",
        label: "Visible on Card",
        type: "checkbox",
        required: false,
        readonly: isViewMode.value,
      }
    );
  }
  return fields;
});

const editFormFields = computed(() => {
  const fields: any[] = [
    {
      key: "prompt_category_id",
      label: "Prompt Category",
      type: "select",
      required: true,
      placeholder: "Select Category",
      options: moduleOptions.value,
      readonly: isViewMode.value
    },
    {
      key: "title",
      label: "Title",
      type: "text",
      required: true,
      placeholder: "Enter title",
      readonly: isViewMode.value
    },
    {
      key: "type_id",
      label: "Type",
      type: "select",
      required: true,
      placeholder: "Select Type",
      options: typeOptions.value,
      readonly: isViewMode.value
    },
    {
      key: "raw_description",
      label: "Base Prompt Description",
      type: "textarea",
      required: true,
      placeholder: "Enter base prompt description",
      readonly: isViewMode.value
    },
    {
      key: "description",
      label: "Final Prompt (Auto-generated)",
      type: "textarea",
      required: false,
      readonly: true,
      placeholder: "This will be auto-generated from base description + selected variables"
    },
  ];

  if (isCardCategoryEdit.value) {
    fields.push(
      {
        key: "filterable",
        label: "Filterable",
        type: "checkbox",
        required: false,
        readonly: isViewMode.value,
      },
      {
        key: "visible_on_card",
        label: "Visible on Card",
        type: "checkbox",
        required: false,
        readonly: isViewMode.value,
      }
    );
  }
  return fields;
});

// --- Validation ---
const isCreateFormValid = computed(
  () =>
    !newItemData.value.title.trim() ||
    !newItemData.value.prompt_category_id.trim() ||
    !newItemData.value.raw_description.trim()
);
const isEditFormValid = computed(
  () =>
    !editingItemData.value.title?.trim() ||
    !editingItemData.value.prompt_category_id?.trim() ||
    !editingItemData.value.raw_description?.trim()
);

// Tab methods
const setActiveTab = async (tab: 'default' | 'byusers') => {
  activeTab.value = tab;
  // Ensure lookup data is fresh before fetching variables
  if (moduleOptions.value.length === 0 || typeOptions.value.length === 0) {
    await fetchModulesAndTypes();
  }
  await fetchPromptVariables();
};

const fetchPromptVariables = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    console.log(`Fetching ${activeTab.value} prompt variables...`);
    const isDefault = activeTab.value === 'default';
    const endpoint = `/common/prompt-variables-filtered?default=${isDefault}`;
    const response = await apiService.get(endpoint);
    console.log('Prompt Variables API Response:', response);
    
    if (Array.isArray(response)) {
      promptVariables.value = response;
      console.log(`Loaded ${promptVariables.value.length} ${activeTab.value} prompt variables`);
    } else if (response && response.data && Array.isArray(response.data)) {
      promptVariables.value = response.data;
      console.log(`Loaded ${promptVariables.value.length} ${activeTab.value} prompt variables`);
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error('Invalid response format');
    }
  } catch (err: any) {
    console.error('Error fetching prompt variables:', err);
    error.value = err?.response?.data?.message || err?.message || 'Failed to load prompt variables';
  } finally {
    isLoading.value = false;
  }
};

// --- Handlers ---
const handleEdit = async (row: any) => {
  isViewMode.value = false;
  
  // Load all variables for parsing
  let allVars: any[] = [];
  try {
    const res = await apiService.get('/common/prompt-variables');
    allVars = (res && res.data && Array.isArray(res.data)) ? res.data : (Array.isArray(res) ? res : []);
  } catch (error) {
    console.error('Error loading variables:', error);
  }
  
  const { rawDescription, selectedVars } = parseExistingDescription(row.description || '', allVars);
  
  editingItemData.value = {
    ...row,
    raw_description: rawDescription,
    selectedVariables: selectedVars,
    prompt_category_id:
      typeof row.prompt_category_id === "object"
        ? row.prompt_category_id._id
        : row.prompt_category_id,
    type_id:
      typeof row.type_id === "object"
        ? row.type_id._id
        : (row.type_id ?? null),
    slug: row.slug ?? toSlug(row.title || ""),
    // default fallbacks for booleans
    filterable: !!row.filterable,
    visible_on_card: !!row.visible_on_card,
  };
  isEditModalOpen.value = true;
};

const handleView = async (row: any) => {
  isViewMode.value = true;
  
  // For view mode, just show the final description as raw description
  const rawDescription = row.raw_description || row.description || '';
  const selectedVars: any[] = [];
  
  editingItemData.value = {
    ...row,
    raw_description: rawDescription,
    selectedVariables: selectedVars,
    filterable: !!row.filterable,
    visible_on_card: !!row.visible_on_card,
    slug: row.slug ?? toSlug(row.title || ""),
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this item?")) {
    await crud.deleteItem(id);
    await fetchPromptVariables(); // Refresh the current tab data
  }
};

// Normalize empty type_id to null before sending
const normalizeTypeId = (v: any) =>
  (typeof v === "string" ? v.trim() : v) ? v : null;

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  newItemData.value.slug = toSlug(newItemData.value.title);
  const payload = {
    ...newItemData.value,
    type_id: normalizeTypeId(newItemData.value.type_id),
  };

  const result = await crud.createItem(payload);
  if (result) {
    closeCreateModal();
    await fetchPromptVariables(); // Refresh the current tab data
    newItemData.value = {
      prompt_category_id: "",
      type_id: null,
      title: "",
      slug: "",
      description: "",
      raw_description: "",
      selectedVariables: [],
      filterable: false,
      visible_on_card: false,
    };
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...rest } = editingItemData.value;

  const payload = {
    ...rest,
    type_id: normalizeTypeId(rest.type_id),
  };

  const result = await crud.updateItem(id, payload);
  if (result) {
    closeEditModal();
    await fetchPromptVariables(); // Refresh the current tab data
  }
};

const openCreateModal = () => {
  isCreateModalOpen.value = true;
  // Initialize with empty data and trigger description update
  setTimeout(() => {
    updateDescriptionWithVariables(newItemData.value);
  }, 100);
};

const onCreateModalShown = () => {
  console.log('Create modal shown, initializing description');
  updateDescriptionWithVariables(newItemData.value);
};

const onEditModalShown = () => {
  console.log('Edit modal shown, initializing description');
  updateDescriptionWithVariables(editingItemData.value);
};

// Automatic variable selection handles slug insertion/removal

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  isViewMode.value = false;
  newItemData.value = {
    prompt_category_id: "",
    type_id: null,
    title: "",
    description: "",
    raw_description: "",
    slug: "",
    selectedVariables: [],
    filterable: false,
    visible_on_card: false,
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = {
    _id: "",
    prompt_category_id: "",
    type_id: null,
    title: "",
    slug: "",   
    description: "",
    raw_description: "",
    selectedVariables: [],
    filterable: false,
    visible_on_card: false,
  };
};

// --- Load data ---
onMounted(async () => {
  // Load the lookup data first, then the prompt variables
  await fetchModulesAndTypes();
  await fetchPromptVariables();
});

const fetchModulesAndTypes = async () => {
  try {
    const res = await apiService.get("common/prompt-categories");
    // Handle both wrapped {.data: [...]} and direct array responses
    moduleOptions.value = (res && res.data && Array.isArray(res.data)) 
      ? res.data 
      : (Array.isArray(res) ? res : []);

    const typ = await apiService.get("common/variable-types");
    typeOptions.value = (typ && typ.data && Array.isArray(typ.data)) 
      ? typ.data 
      : (Array.isArray(typ) ? typ : []);
    
    // Load all variables for the variable picker
    const vars = await apiService.get("common/prompt-variables");
    allVariables.value = (vars && vars.data && Array.isArray(vars.data)) 
      ? vars.data 
      : (Array.isArray(vars) ? vars : []);
    
    console.log(allVariables.value,"allVariables.value");
  } catch {
    moduleOptions.value = [];
    typeOptions.value = [];
    allVariables.value = [];
  }
};
</script>


<style scoped>
/* Modal enhancements */
:deep(.modal-xl) {
  max-width: 90vw;
}

:deep(.modal-xl .modal-body) {
  max-height: 70vh;
  overflow-y: auto;
}

/* Form layout improvements */
:deep(.variable-picker) {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
  margin-top: 1rem;
}

/* Make textarea descriptions larger in full-width modals */
:deep(.modal-xl textarea) {
  min-height: 120px;
}

/* Two-column layout for large modals */
:deep(.modal-xl .modal-body) {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

@media (min-width: 992px) {
  :deep(.modal-xl .modal-body) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  
  :deep(.modal-xl .variable-picker-wrapper) {
    /* Keep on the right side by occupying the second column */
    grid-column: 2;
  }
}
</style>
